/**
 * useBikeLayer / usePreWarmBikeTiles
 *
 * Tile queue architecture — all state is module-level and shared.
 *
 * Priority strategy
 * ─────────────────
 * Active-layer tiles use `Date.now()` as their priority value.  Because each
 * idle event fires at a later timestamp, the current viewport's tiles are
 * always numerically higher than tiles queued from any previous pan position.
 * The queue re-sorts before every dequeue, so as soon as a worker slot opens
 * it immediately picks up the most recently requested tiles — never work for
 * an area the user has already scrolled away from.
 *
 * Far-tile abort
 * ──────────────
 * When the user pans significantly, in-flight fetches for the old area are
 * aborted client-side.  The Cloud Function keeps running and caches the result
 * in Firebase Storage, so the server cache still warms — we just free the
 * worker slot immediately so it can start on the new location.
 *
 * Concurrency
 * ───────────
 * MAX_WORKERS = 2 matches Overpass's 2-slot rate limit.
 */

import { useEffect, useRef } from 'react';
import { useMap } from '@vis.gl/react-google-maps';

const TILE_ZOOM = 11;

// ── Module-level shared state ────────────────────────────────────────────────

/** key → GeoJSON FeatureCollection */
const tileCache = new Map();

/** Keys of tiles whose fetch is currently running */
const inFlight = new Set();

/** key → AbortController for currently running fetches */
const tileAbortControllers = new Map();

/**
 * Fetch queue.  Each entry: { tile, priority }.
 * Higher priority = dequeued first.
 * Re-sorted before every dequeue so late priority bumps take effect immediately.
 */
const queue = [];

let activeWorkers = 0;
const MAX_WORKERS = 2;

/** Callbacks fired when any tile finishes (cache hit or fresh fetch). */
const completionListeners = new Set();

// ── Geometry helpers ─────────────────────────────────────────────────────────

function latLngToTile(lat, lng, zoom) {
  const x = Math.floor(((lng + 180) / 360) * 2 ** zoom);
  const latRad = (lat * Math.PI) / 180;
  const y = Math.floor(
    ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * 2 ** zoom
  );
  return { x, y };
}

function getTilesForBounds(bounds, bufferFactor = 0) {
  const ne = bounds.getNorthEast();
  const sw = bounds.getSouthWest();
  const latSpan = ne.lat() - sw.lat();
  const lngSpan = ne.lng() - sw.lng();

  const north = Math.min(85, ne.lat() + latSpan * bufferFactor);
  const south = Math.max(-85, sw.lat() - latSpan * bufferFactor);
  const east  = ne.lng() + lngSpan * bufferFactor;
  const west  = sw.lng() - lngSpan * bufferFactor;

  const nwTile = latLngToTile(north, west, TILE_ZOOM);
  const seTile = latLngToTile(south, east, TILE_ZOOM);

  const tiles = [];
  for (let x = nwTile.x; x <= seTile.x; x++) {
    for (let y = nwTile.y; y <= seTile.y; y++) {
      tiles.push({ x, y, z: TILE_ZOOM });
    }
  }
  return tiles;
}

const tileKey = (t) => `${t.z}_${t.x}_${t.y}`;

// ── Fetch primitives ─────────────────────────────────────────────────────────

async function fetchTileFromApi(tile, signal) {
  try {
    const resp = await fetch(
      `/api/bike-tiles?x=${tile.x}&y=${tile.y}&z=${tile.z}`,
      { signal }
    );
    if (!resp.ok) {
      console.warn('[BikeLayer] API error for tile', tileKey(tile), resp.status);
      return null;
    }
    const ct = resp.headers.get('content-type') || '';
    if (!ct.includes('json')) {
      console.warn(
        '[BikeLayer] Non-JSON response for tile', tileKey(tile),
        `(${ct || 'no content-type'}) — is dev-server running?`
      );
      return null;
    }
    return await resp.json();
  } catch (e) {
    // AbortError is expected when we liberate a worker slot — not a real error
    if (e.name !== 'AbortError') {
      console.warn('[BikeLayer] Fetch failed for tile', tileKey(tile), e.message);
    }
    return null;
  }
}

async function runWorker() {
  if (activeWorkers >= MAX_WORKERS) return;
  activeWorkers++;

  while (queue.length > 0) {
    // Re-sort on every iteration so late priority bumps (from a new idle event
    // that arrived while we were fetching) take effect immediately.
    queue.sort((a, b) => b.priority - a.priority);
    const { tile } = queue.shift();
    const key = tileKey(tile);

    if (tileCache.has(key)) {
      // Already fetched by the other worker — just notify and continue
      inFlight.delete(key);
      completionListeners.forEach((cb) => cb());
      continue;
    }

    const controller = new AbortController();
    tileAbortControllers.set(key, controller);

    const data = await fetchTileFromApi(tile, controller.signal);

    tileAbortControllers.delete(key);
    inFlight.delete(key);
    if (data) tileCache.set(key, data);
    completionListeners.forEach((cb) => cb());
  }

  activeWorkers--;
}

/**
 * Enqueue new tiles and bump the priority of tiles already waiting in the queue.
 * This ensures that re-visiting an area immediately promotes its tiles to the
 * front even if they were enqueued earlier at a lower priority.
 */
function enqueueTiles(tiles, priority = 1) {
  const tileSet = new Set(tiles.map(tileKey));

  // Boost priority of already-queued tiles
  for (const item of queue) {
    if (tileSet.has(tileKey(item.tile)) && priority > item.priority) {
      item.priority = priority;
    }
  }

  // Add tiles not already cached or in-flight
  let added = false;
  for (const tile of tiles) {
    const key = tileKey(tile);
    if (tileCache.has(key) || inFlight.has(key)) continue;
    inFlight.add(key);
    queue.push({ tile, priority });
    added = true;
  }

  if (added) runWorker();
}

/**
 * Abort any in-flight fetches for tiles that fall outside the given extended
 * viewport.  The Cloud Function keeps running and caches the result in Firebase
 * Storage, so the server-side cache still warms — we just free the local worker
 * slot so it can immediately start on the current viewport's tiles.
 */
function abortFarTiles(bounds) {
  // Generous 2.5x buffer — only abort tiles that are genuinely far away,
  // not just slightly outside the current view
  const keepSet = new Set(getTilesForBounds(bounds, 2.5).map(tileKey));
  for (const [key, controller] of tileAbortControllers) {
    if (!keepSet.has(key)) {
      controller.abort(); // worker catches AbortError, cleans up, picks next tile
    }
  }
}

// ── Hooks ────────────────────────────────────────────────────────────────────

/**
 * Manages bike trail fetching for the active layer.
 *
 * Calls onTileReady(key, geojson) incrementally for each tile that arrives.
 * Calls onLoadingProgress(0–100 | null) to drive the progress bar.
 * No React state — tile arrivals never cause a React re-render.
 */
export function useBikeLayer(enabled, onLoadingProgress, onTileReady) {
  const map = useMap();

  const onProgressRef  = useRef(onLoadingProgress);
  const onTileReadyRef = useRef(onTileReady);
  onProgressRef.current  = onLoadingProgress;
  onTileReadyRef.current = onTileReady;

  /** Keys already delivered to onTileReady this session */
  const deliveredRef = useRef(new Set());

  /**
   * Deliver any newly-cached viewport tiles and update the progress bar.
   * Called after every tile completion and on every map idle.
   */
  const syncRef = useRef(null);
  syncRef.current = () => {
    if (!map || !enabled) return;
    const bounds = map.getBounds();
    if (!bounds) return;

    const tiles   = getTilesForBounds(bounds, 1.5);
    const total   = tiles.length;
    const loaded  = tiles.filter((t) => tileCache.has(tileKey(t))).length;
    const pending = tiles.filter((t) => inFlight.has(tileKey(t))).length;

    for (const tile of tiles) {
      const key = tileKey(tile);
      if (tileCache.has(key) && !deliveredRef.current.has(key)) {
        deliveredRef.current.add(key);
        onTileReadyRef.current?.(key, tileCache.get(key));
      }
    }

    if (pending === 0) {
      onProgressRef.current?.(null);
    } else {
      onProgressRef.current?.(Math.round((loaded / total) * 100));
    }
  };

  // Subscribe to tile completions
  useEffect(() => {
    if (!enabled) {
      deliveredRef.current.clear();
      onProgressRef.current?.(null);
      return undefined;
    }
    const listener = () => syncRef.current?.();
    completionListeners.add(listener);
    return () => completionListeners.delete(listener);
  }, [enabled]);

  // On map idle: abort far tiles, enqueue current tiles with timestamp priority
  useEffect(() => {
    if (!map || !enabled) return undefined;

    const handler = () => {
      const bounds = map.getBounds();
      if (!bounds) return;

      // Free worker slots occupied by tiles for areas we've left
      abortFarTiles(bounds);

      const now = Date.now();

      // Split into viewport (renders first) and buffer (background).
      // Using now+1 vs now means viewport tiles always sort above buffer tiles
      // in the priority queue, so the visible area loads before the buffer areas.
      const viewportTiles = getTilesForBounds(bounds, 0);
      const allTiles      = getTilesForBounds(bounds, 1.5);
      const viewportSet   = new Set(viewportTiles.map(tileKey));
      const bufferTiles   = allTiles.filter((t) => !viewportSet.has(tileKey(t)));

      const viewportUncached = viewportTiles.filter((t) => !tileCache.has(tileKey(t)));
      const bufferUncached   = bufferTiles.filter((t) => !tileCache.has(tileKey(t)));

      if (viewportUncached.length > 0 || bufferUncached.length > 0) {
        const loaded = allTiles.length - viewportUncached.length - bufferUncached.length;
        onProgressRef.current?.(Math.round((loaded / allTiles.length) * 100));
      }

      // Viewport tiles enqueued with slightly higher priority — always picked first
      enqueueTiles(viewportUncached, now + 1);
      enqueueTiles(bufferUncached, now);

      syncRef.current?.();
    };

    const listener = map.addListener('idle', handler);
    handler();
    return () => window.google?.maps?.event?.removeListener(listener);
  }, [map, enabled]);
}

/**
 * Silently pre-warms the tile cache whenever the map settles.
 * Always uses priority 1 so it never competes with the active layer.
 */
export function usePreWarmBikeTiles() {
  const map = useMap();

  useEffect(() => {
    if (!map) return undefined;

    const listener = map.addListener('idle', () => {
      const bounds = map.getBounds();
      if (!bounds) return;
      enqueueTiles(getTilesForBounds(bounds, 1.5), 1);
    });

    return () => window.google?.maps?.event?.removeListener(listener);
  }, [map]);
}
